import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Save as SaveIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  VideoLibrary as VideoIcon,
  Notifications as NotificationsIcon,
  Palette as ThemeIcon,
  Storage as StorageIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { settingsService, socialService } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface ConnectedAccount {
  id: string;
  platform: string;
  platform_username: string;
  platform_user_id: string;
  connected_at: string;
  token_expires_at: string | null;
  is_active: number;
}

const Settings: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  
  // Settings state
  const [generalSettings, setGeneralSettings] = useState({
    app_name: 'AutoReelr',
    default_language: 'en',
    timezone: 'UTC',
    enable_notifications: true,
    enable_auto_publishing: true,
    default_video_quality: '1080p',
    default_video_duration: 60,
    max_concurrent_processing: 3,
    enable_watermark: false,
    watermark_text: '',
    watermark_position: 'bottom-right',
  });
  
  const SUPPORTED_PLATFORMS = [
    { value: 'tiktok', label: 'TikTok', icon: null },
    { value: 'instagram', label: 'Instagram', icon: null },
    { value: 'youtube', label: 'YouTube', icon: null },
  ];
  
  const [videoSettings, setVideoSettings] = useState({
    video_quality: '1080p',
    video_format: 'mp4',
    video_codec: 'h264',
    audio_codec: 'aac',
    frame_rate: 30,
    bitrate: 'auto',
    enable_subtitles: true,
    subtitle_style: 'default',
    enable_transitions: true,
    transition_duration: 0.5,
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    push_notifications: true,
    notify_on_processing_complete: true,
    notify_on_publishing_complete: true,
    notify_on_error: true,
    notify_on_schedule_trigger: false,
    daily_summary: true,
    weekly_report: true,
  });

  useEffect(() => {
    fetchSettings();
    fetchConnectedAccounts();

    // Handle OAuth redirect
    const tab = searchParams.get('tab');
    const connected = searchParams.get('connected');
    const success = searchParams.get('success');
    const error = searchParams.get('error');

    if (tab === 'social') {
      setTabValue(1);
      if (connected && success === 'true') {
        enqueueSnackbar(`${connected} connected successfully!`, { variant: 'success' });
      }
      if (error) {
        enqueueSnackbar(`Connection failed: ${error}`, { variant: 'error' });
      }
    }
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsService.get();
      if (response.data) {
        // Update state with fetched settings
        if (response.data.general) {
          setGeneralSettings({ ...generalSettings, ...response.data.general });
        }
        if (response.data.video) {
          setVideoSettings({ ...videoSettings, ...response.data.video });
        }
        if (response.data.notifications) {
          setNotificationSettings({ ...notificationSettings, ...response.data.notifications });
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Use default values
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectedAccounts = async () => {
    try {
      const response = await socialService.accounts();
      setConnectedAccounts(response.data?.accounts || []);
    } catch (error) {
      console.error('Failed to fetch connected accounts:', error);
    }
  };

  const handleConnectPlatform = (platform: string) => {
    setConnectingPlatform(platform);
    socialService.connect(platform);
  };

  const handleDisconnectAccount = async (accountId: string, platform: string) => {
    try {
      await socialService.disconnect(accountId);
      enqueueSnackbar(`${platform} disconnected`, { variant: 'success' });
      fetchConnectedAccounts();
    } catch (error) {
      enqueueSnackbar('Failed to disconnect account', { variant: 'error' });
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const settings = {
        general: generalSettings,
        video: videoSettings,
        notifications: notificationSettings,
      };
      
      await settingsService.update(settings);
      enqueueSnackbar('Settings saved successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to save settings:', error);
      enqueueSnackbar('Failed to save settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <InstagramIcon />;
      case 'youtube':
        return <YouTubeIcon />;
      case 'facebook':
        return <FacebookIcon />;
      case 'twitter':
        return <TwitterIcon />;
      case 'linkedin':
        return <LinkedInIcon />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Settings
          </Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </Box>

        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="General" icon={<ThemeIcon />} iconPosition="start" />
            <Tab label="Social Media" icon={<InstagramIcon />} iconPosition="start" />
            <Tab label="Video Processing" icon={<VideoIcon />} iconPosition="start" />
            <Tab label="Notifications" icon={<NotificationsIcon />} iconPosition="start" />
          </Tabs>

          {/* General Settings */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Application Settings
                    </Typography>
                    <Box mt={2}>
                      <TextField
                        fullWidth
                        label="Application Name"
                        value={generalSettings.app_name}
                        onChange={(e) => setGeneralSettings({ ...generalSettings, app_name: e.target.value })}
                        margin="normal"
                      />
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Default Language</InputLabel>
                        <Select
                          value={generalSettings.default_language}
                          onChange={(e) => setGeneralSettings({ ...generalSettings, default_language: e.target.value })}
                          label="Default Language"
                        >
                          <MenuItem value="en">English</MenuItem>
                          <MenuItem value="es">Spanish</MenuItem>
                          <MenuItem value="fr">French</MenuItem>
                          <MenuItem value="de">German</MenuItem>
                          <MenuItem value="ja">Japanese</MenuItem>
                          <MenuItem value="zh">Chinese</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Timezone</InputLabel>
                        <Select
                          value={generalSettings.timezone}
                          onChange={(e) => setGeneralSettings({ ...generalSettings, timezone: e.target.value })}
                          label="Timezone"
                        >
                          <MenuItem value="UTC">UTC</MenuItem>
                          <MenuItem value="America/New_York">Eastern Time</MenuItem>
                          <MenuItem value="America/Chicago">Central Time</MenuItem>
                          <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                          <MenuItem value="Europe/London">London</MenuItem>
                          <MenuItem value="Asia/Tokyo">Tokyo</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Processing Settings
                    </Typography>
                    <Box mt={2}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Default Video Quality</InputLabel>
                        <Select
                          value={generalSettings.default_video_quality}
                          onChange={(e) => setGeneralSettings({ ...generalSettings, default_video_quality: e.target.value })}
                          label="Default Video Quality"
                        >
                          <MenuItem value="480p">480p</MenuItem>
                          <MenuItem value="720p">720p</MenuItem>
                          <MenuItem value="1080p">1080p</MenuItem>
                          <MenuItem value="4k">4K</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Default Video Duration (seconds)"
                        type="number"
                        value={generalSettings.default_video_duration}
                        onChange={(e) => setGeneralSettings({ ...generalSettings, default_video_duration: parseInt(e.target.value) })}
                        margin="normal"
                      />
                      <TextField
                        fullWidth
                        label="Max Concurrent Processing"
                        type="number"
                        value={generalSettings.max_concurrent_processing}
                        onChange={(e) => setGeneralSettings({ ...generalSettings, max_concurrent_processing: parseInt(e.target.value) })}
                        margin="normal"
                        helperText="Maximum number of videos to process simultaneously"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Features
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <NotificationsIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Enable Notifications"
                          secondary="Receive alerts about processing and publishing"
                        />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={generalSettings.enable_notifications}
                            onChange={(e) => setGeneralSettings({ ...generalSettings, enable_notifications: e.target.checked })}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <StorageIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Enable Auto Publishing"
                          secondary="Automatically publish reels when scheduled"
                        />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={generalSettings.enable_auto_publishing}
                            onChange={(e) => setGeneralSettings({ ...generalSettings, enable_auto_publishing: e.target.checked })}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <VideoIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Enable Watermark"
                          secondary="Add watermark to generated videos"
                        />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={generalSettings.enable_watermark}
                            onChange={(e) => setGeneralSettings({ ...generalSettings, enable_watermark: e.target.checked })}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </List>
                    {generalSettings.enable_watermark && (
                      <Box mt={2}>
                        <TextField
                          fullWidth
                          label="Watermark Text"
                          value={generalSettings.watermark_text}
                          onChange={(e) => setGeneralSettings({ ...generalSettings, watermark_text: e.target.value })}
                          margin="normal"
                        />
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Watermark Position</InputLabel>
                          <Select
                            value={generalSettings.watermark_position}
                            onChange={(e) => setGeneralSettings({ ...generalSettings, watermark_position: e.target.value })}
                            label="Watermark Position"
                          >
                            <MenuItem value="top-left">Top Left</MenuItem>
                            <MenuItem value="top-right">Top Right</MenuItem>
                            <MenuItem value="bottom-left">Bottom Left</MenuItem>
                            <MenuItem value="bottom-right">Bottom Right</MenuItem>
                            <MenuItem value="center">Center</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Social Media Settings */}
          <TabPanel value={tabValue} index={1}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Connect your social media accounts to enable auto-publishing. Click "Connect" to authorize AutoReelr via OAuth.
            </Alert>

            <Typography variant="h6" gutterBottom>
              Connected Accounts
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {connectedAccounts.length === 0 ? (
                <Grid item xs={12}>
                  <Typography color="text.secondary">
                    No accounts connected yet. Use the buttons below to connect your social media accounts.
                  </Typography>
                </Grid>
              ) : (
                connectedAccounts.map((account) => (
                  <Grid item xs={12} sm={6} key={account.id}>
                    <Card>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            {getPlatformIcon(account.platform)}
                            <Box>
                              <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                                {account.platform}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                @{account.platform_username}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip label="Connected" color="success" size="small" icon={<CheckIcon />} />
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleDisconnectAccount(account.id, account.platform)}
                            >
                              Disconnect
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Connect New Account
            </Typography>

            <Grid container spacing={2}>
              {SUPPORTED_PLATFORMS.map((platform) => {
                const isConnected = connectedAccounts.some((a) => a.platform === platform.value);
                return (
                  <Grid item xs={12} sm={4} key={platform.value}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Box sx={{ mb: 2 }}>
                          {getPlatformIcon(platform.value)}
                        </Box>
                        <Typography variant="h6" gutterBottom>
                          {platform.label}
                        </Typography>
                        <Button
                          variant={isConnected ? 'outlined' : 'contained'}
                          fullWidth
                          onClick={() => handleConnectPlatform(platform.value)}
                          disabled={connectingPlatform === platform.value}
                        >
                          {isConnected ? 'Reconnect' : 'Connect'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </TabPanel>

          {/* Video Processing Settings */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Video Output Settings
                    </Typography>
                    <Box mt={2}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Video Quality</InputLabel>
                        <Select
                          value={videoSettings.video_quality}
                          onChange={(e) => setVideoSettings({ ...videoSettings, video_quality: e.target.value })}
                          label="Video Quality"
                        >
                          <MenuItem value="480p">480p (SD)</MenuItem>
                          <MenuItem value="720p">720p (HD)</MenuItem>
                          <MenuItem value="1080p">1080p (Full HD)</MenuItem>
                          <MenuItem value="4k">4K (Ultra HD)</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Video Format</InputLabel>
                        <Select
                          value={videoSettings.video_format}
                          onChange={(e) => setVideoSettings({ ...videoSettings, video_format: e.target.value })}
                          label="Video Format"
                        >
                          <MenuItem value="mp4">MP4</MenuItem>
                          <MenuItem value="mov">MOV</MenuItem>
                          <MenuItem value="avi">AVI</MenuItem>
                          <MenuItem value="webm">WebM</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Frame Rate (FPS)"
                        type="number"
                        value={videoSettings.frame_rate}
                        onChange={(e) => setVideoSettings({ ...videoSettings, frame_rate: parseInt(e.target.value) })}
                        margin="normal"
                      />
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Bitrate</InputLabel>
                        <Select
                          value={videoSettings.bitrate}
                          onChange={(e) => setVideoSettings({ ...videoSettings, bitrate: e.target.value })}
                          label="Bitrate"
                        >
                          <MenuItem value="auto">Auto</MenuItem>
                          <MenuItem value="low">Low (2 Mbps)</MenuItem>
                          <MenuItem value="medium">Medium (5 Mbps)</MenuItem>
                          <MenuItem value="high">High (10 Mbps)</MenuItem>
                          <MenuItem value="very_high">Very High (20 Mbps)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Encoding Settings
                    </Typography>
                    <Box mt={2}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Video Codec</InputLabel>
                        <Select
                          value={videoSettings.video_codec}
                          onChange={(e) => setVideoSettings({ ...videoSettings, video_codec: e.target.value })}
                          label="Video Codec"
                        >
                          <MenuItem value="h264">H.264 (Most Compatible)</MenuItem>
                          <MenuItem value="h265">H.265 (Better Compression)</MenuItem>
                          <MenuItem value="vp9">VP9 (Web Optimized)</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Audio Codec</InputLabel>
                        <Select
                          value={videoSettings.audio_codec}
                          onChange={(e) => setVideoSettings({ ...videoSettings, audio_codec: e.target.value })}
                          label="Audio Codec"
                        >
                          <MenuItem value="aac">AAC</MenuItem>
                          <MenuItem value="mp3">MP3</MenuItem>
                          <MenuItem value="opus">Opus</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Video Features
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Enable Subtitles"
                          secondary="Automatically generate subtitles for videos"
                        />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={videoSettings.enable_subtitles}
                            onChange={(e) => setVideoSettings({ ...videoSettings, enable_subtitles: e.target.checked })}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Enable Transitions"
                          secondary="Add smooth transitions between scenes"
                        />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={videoSettings.enable_transitions}
                            onChange={(e) => setVideoSettings({ ...videoSettings, enable_transitions: e.target.checked })}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </List>
                    {videoSettings.enable_transitions && (
                      <Box mt={2}>
                        <TextField
                          fullWidth
                          label="Transition Duration (seconds)"
                          type="number"
                          value={videoSettings.transition_duration}
                          onChange={(e) => setVideoSettings({ ...videoSettings, transition_duration: parseFloat(e.target.value) })}
                          inputProps={{ step: 0.1, min: 0.1, max: 2 }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Notification Settings */}
          <TabPanel value={tabValue} index={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notification Preferences
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Notifications"
                      secondary="Receive notifications via email"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.email_notifications}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, email_notifications: e.target.checked })}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Push Notifications"
                      secondary="Receive browser push notifications"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.push_notifications}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, push_notifications: e.target.checked })}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider sx={{ my: 2 }} />
                  <ListItem>
                    <ListItemText
                      primary="Processing Complete"
                      secondary="Notify when video processing is finished"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.notify_on_processing_complete}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_on_processing_complete: e.target.checked })}
                        disabled={!notificationSettings.email_notifications && !notificationSettings.push_notifications}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Publishing Complete"
                      secondary="Notify when content is published"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.notify_on_publishing_complete}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_on_publishing_complete: e.target.checked })}
                        disabled={!notificationSettings.email_notifications && !notificationSettings.push_notifications}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Error Notifications"
                      secondary="Notify when errors occur"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.notify_on_error}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_on_error: e.target.checked })}
                        disabled={!notificationSettings.email_notifications && !notificationSettings.push_notifications}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Schedule Triggers"
                      secondary="Notify when scheduled posts are triggered"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.notify_on_schedule_trigger}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_on_schedule_trigger: e.target.checked })}
                        disabled={!notificationSettings.email_notifications && !notificationSettings.push_notifications}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider sx={{ my: 2 }} />
                  <ListItem>
                    <ListItemText
                      primary="Daily Summary"
                      secondary="Receive daily activity summary"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.daily_summary}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, daily_summary: e.target.checked })}
                        disabled={!notificationSettings.email_notifications}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Weekly Report"
                      secondary="Receive weekly performance report"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={notificationSettings.weekly_report}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, weekly_report: e.target.checked })}
                        disabled={!notificationSettings.email_notifications}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </TabPanel>
        </Paper>
      </Box>
    </Container>
  );
};

export default Settings;